// Curvas EKH. La señal es la amplitud firmada del fabricante (heartRange / respiratoryRange): el
// detalle técnico vive en el tooltip y en la sección técnica de la ficha, no en el cuerpo clínico.
// El inicio no se rellena con ceros y los huecos se dibujan como CORTES.
import { rangoSimetrico } from "@/lib/ekh/vista";

const W = 600;
const H = 120;

const NOTA_TECNICA =
  "Señal de amplitud firmada por el fabricante, sin unidad acreditada. Conserva ceros, huecos y " +
  "tiempos tal como llegaron. No es un ECG ni un diagnóstico.";

// `null` = HUECO declarado: la línea se CORTA ahí (nunca se interpola ni se rellena con ceros).
function segmentar(muestras, w, h) {
  const finitos = muestras.filter((v) => typeof v === "number");
  const max = rangoSimetrico(finitos);
  const paso = muestras.length > 1 ? w / (muestras.length - 1) : w;
  const segmentos = [];
  let actual = [];
  muestras.forEach((v, i) => {
    if (typeof v !== "number") {
      if (actual.length) segmentos.push(actual);
      actual = [];
      return;
    }
    actual.push(`${(i * paso).toFixed(2)},${(h / 2 - (v / max) * (h / 2 - 6)).toFixed(2)}`);
  });
  if (actual.length) segmentos.push(actual);
  return { segmentos, finitos };
}

export function CurvaAmplitud({ muestras = [], color = "#0A84FF", titulo, enVivo = false,
                               testId, sinSenal = false }) {
  const { segmentos, finitos } = segmentar(muestras, W, H);
  return (
    <figure data-testid={testId} data-sin-senal={sinSenal ? "si" : "no"}
      className="gv2-panel rounded-xl p-3" title={NOTA_TECNICA}>
      <figcaption className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#012472]">{titulo}</span>
        {enVivo && !sinSenal && finitos.length > 0 && (
          <span className="gv2-chip gv2-chip-live inline-flex items-center">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" /> En vivo
          </span>
        )}
      </figcaption>
      {finitos.length === 0 ? (
        <p className="py-6 text-center text-xs text-slate-400">Esperando señal del sensor.</p>
      ) : sinSenal ? (
        <p data-testid={`${testId}-sin-senal`} className="py-6 text-center text-xs text-slate-400">
          Sin señal en este momento.
        </p>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="mt-1 h-24 w-full" preserveAspectRatio="none">
          <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="currentColor" strokeOpacity="0.12" />
          {segmentos.map((seg, i) => (
            <polyline key={i} points={seg.join(" ")} fill="none" stroke={color}
              strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
      )}
    </figure>
  );
}

// Mini curva del mosaico: misma señal, tamaño de tarjeta. Sin ejes, sin textos técnicos.
export function MiniCurva({ muestras = [], color = "#0A84FF", testId, etiqueta }) {
  const { segmentos, finitos } = segmentar(muestras, 120, 28);
  const sinSenal = finitos.length > 0 && finitos.every((v) => !v);
  if (!finitos.length || sinSenal) {
    return (
      <div data-testid={testId} data-curva="vacia" aria-label={etiqueta}
        className="mt-1 h-7 w-full rounded bg-[color:var(--gv-raised,#eef2f7)]" />
    );
  }
  return (
    <svg data-testid={testId} data-curva="viva" viewBox="0 0 120 28" role="img" aria-label={etiqueta}
      className="mt-1 h-7 w-full" preserveAspectRatio="none">
      {segmentos.map((seg, i) => (
        <polyline key={i} points={seg.join(" ")} fill="none" stroke={color}
          strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}

export function TendenciaFrecuencia({ segmentos = [], color = "#FF453A", titulo, unidad, testId }) {
  const todos = segmentos.flat();
  if (!todos.length) {
    return (
      <figure data-testid={testId} className="gv2-panel rounded-xl p-3">
        <figcaption className="text-[11px] font-extrabold uppercase tracking-wider text-[#012472]">{titulo}</figcaption>
        <p className="py-6 text-center text-xs text-slate-400">Sin lecturas en el periodo consultado.</p>
      </figure>
    );
  }
  const t0 = Math.min(...todos.map((p) => p.t));
  const t1 = Math.max(...todos.map((p) => p.t));
  const vmin = Math.min(...todos.map((p) => p.v));
  const vmax = Math.max(...todos.map((p) => p.v));
  const spanT = t1 - t0 || 1;
  // Serie PLANA (un solo valor en toda la ventana): se dibuja centrada, no pegada al borde.
  const plana = vmax === vmin;
  const spanV = vmax - vmin || 1;
  const px = (p) => `${(((p.t - t0) / spanT) * W).toFixed(2)},${(plana ? H / 2 : H - 8 - ((p.v - vmin) / spanV) * (H - 20)).toFixed(2)}`;
  return (
    <figure data-testid={testId} className="gv2-panel rounded-xl p-3"
      title="Valores reportados por el fabricante, con los huecos sin interpolar.">
      <figcaption className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#012472]">{titulo}</span>
        <span className="tabular-nums text-[10px] text-slate-400">{vmin}–{vmax} {unidad}</span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-1 h-24 w-full" preserveAspectRatio="none">
        {segmentos.map((seg, i) => (
          <polyline key={i} points={seg.map(px).join(" ")} fill="none" stroke={color}
            strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
    </figure>
  );
}
